import { useApolloClient } from '@apollo/client';
import { notification } from 'antd';
import imageCompression from 'browser-image-compression';
import { BYTES_PER_MB, MAX_FILE_SIZE_MB } from 'constants/file';
import { MAX_IMAGE_SIZE_MB, MAX_IMAGE_THUMBNAIL_DIMENSION } from 'constants/image';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import FileAction from 'redux/file/file.actions';
import { DELETE_FILES } from 'services/graphql/mutations/files';
import { GET_MANY_SIGNED_URLS } from 'services/graphql/queries/signedUrls.query';
import { IUploadFile } from 'types/common';
import { ISignedUrls } from 'types/signedUrls';
import { convertFilesToDbFormat, getUpdatedDeletedFiles } from 'utils';
import pdfjsLib from 'utils/pdfjsConfig';

import UpdateProfileFormTemplate from '../UpdateProfileFormTemplate';
import { IRecordFormProps } from './types';

const RecordForm: React.FC<IRecordFormProps> = ({
  validationSchema,
  closeModal,
  record,
  userId,
  mutations,
  fields,
  documentType,
  refetchUserData,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const apolloClient = useApolloClient();

  const onCompleteFileUploadingAction = (isUploadSuccess: boolean, err?: any) => {
    setLoading(false);
    closeModal();
    if (isUploadSuccess) {
      notification.success({
        message: 'Update profile successfully!',
        key: 'updateProfile',
        duration: 2,
      });
    } else {
      console.error(err);
      notification.error({
        message: 'Update failed! Please try again.',
        key: 'updateProfile',
        duration: 2,
      });
    }
  };

  const onUploadFileSuccess = async () => {
    // todo: save data to b.e
    // Mutate files
    try {
      refetchUserData?.();
      onCompleteFileUploadingAction(true);
    } catch (err) {
      onCompleteFileUploadingAction(false, err);
    }
  };

  const readFileData = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          resolve(e.target.result);
        };
        reader.onerror = (err) => {
          reject(err);
        };
        reader.readAsDataURL(file);
      } catch (err) {
        reject(err);
      }
    });

  const getPdfThumbnailRatio = (viewport: any) => {
    const { height } = viewport;
    const { width } = viewport;
    const max = height > width ? height : width;
    return MAX_IMAGE_THUMBNAIL_DIMENSION / max;
  };

  /**
   * Try catch
   * @param file File
   * @returns File \
   * get first page of pdf doc and convert to jpg image file
   */
  const getPdfThumbnail = async (file: any): Promise<File | Error> =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        let imageURL = '';
        const data = await readFileData(file);
        const pdf = await pdfjsLib.getDocument(data).promise;
        const canvas = document.createElement('canvas');

        if (pdf.numPages > 0) {
          // only get first page
          const page = await pdf.getPage(1);
          let viewport = page.getViewport({ scale: 1 });
          // scale thumbnail image
          viewport = page.getViewport({ scale: getPdfThumbnailRatio(viewport) });
          const ct = canvas.getContext('2d');
          const context: CanvasRenderingContext2D = ct === null ? new CanvasRenderingContext2D() : ct;
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          // render image
          await page.render({ canvasContext: context, viewport }).promise;
          // convert to base64
          imageURL = canvas.toDataURL();
        }

        // remove canvas
        canvas.remove();
        const res = await fetch(imageURL);
        const blob = await res.blob();
        const ext = file.name.split('.').pop();
        const fileName = file.name.slice(0, file.name.length - (ext.length + 1));
        resolve(new File([blob], `${fileName}_thumbnail.jpg`, { type: 'image/jpeg' }));
      } catch (err) {
        console.log('OUTPUT ~ file: RecordForm.tsx ~ line 140 ~ newPromise ~ err', err);

        reject(err);
      }
    });
  // upload file
  const uploadFile = async (uploadDestinationInfo: ISignedUrls[], files: IUploadFile[], documentId: string) => {
    const fileTypes: string[] = [];
    const fileOrigins: (File | Blob)[] = [];
    const thumbnailOrigins: File[] = [];
    const urls: string[] = [];
    const thumbnailUrls: string[] = [];

    try {
      for (let i = 0; i < uploadDestinationInfo.length; i += 1) {
        const info = uploadDestinationInfo[i];
        urls.push(info.urls[0]);
        thumbnailUrls.push(info.thumbnailUrl);
        fileTypes.push(files[i].type);

        const file = files[i];
        // Try catch
        if (file.originFileObj && file.size / BYTES_PER_MB < MAX_FILE_SIZE_MB) {
          let thumbnailFile: File | Error;
          if (file.type === 'application/pdf') {
            // pdf file => get thumbnail
            // eslint-disable-next-line no-await-in-loop
            thumbnailFile = await getPdfThumbnail(file.originFileObj);
          } else {
            // image file => resize thumbnail
            // eslint-disable-next-line no-await-in-loop
            thumbnailFile = await imageCompression(file.originFileObj as File, {
              maxSizeMB: MAX_IMAGE_SIZE_MB,
              maxWidthOrHeight: MAX_IMAGE_THUMBNAIL_DIMENSION,
              fileType: 'image/jpeg',
            });
          }
          fileOrigins.push(file.originFileObj);

          if (!(thumbnailFile instanceof Error)) {
            thumbnailOrigins.push(thumbnailFile);
          }
        }
      }

      dispatch(
        FileAction.uploadFileStart(
          {
            thumbnailUrls,
            files: fileOrigins,
            thumbnails: thumbnailOrigins,
            urls,
            fileTypes,
            documentId,
          },
          {
            callbackOnSuccess: () => {
              onUploadFileSuccess();
            },
            callbackOnFailure: (err) => {
              onCompleteFileUploadingAction(false, err);
            },
          },
        ),
      );
    } catch (err) {
      onCompleteFileUploadingAction(false, err);
    }
  };

  // delete files
  const deleteFile = async (deletedFiles: any[]) =>
    apolloClient.mutate({
      mutation: DELETE_FILES,
      variables: {
        ids: deletedFiles.map((f) => get(f, 'uid', '')),
      },
    });

  // get signed urls from s3
  const getSignedUrlsFunction = async (files: Array<IUploadFile>) => {
    const convertedFiles = convertFilesToDbFormat(files, { userId });
    return apolloClient.query({
      query: GET_MANY_SIGNED_URLS,
      variables: { fileList: convertedFiles },
      fetchPolicy: 'network-only',
    });
    // await getSignedUrls({ variables: { fileList: convertedFiles } });
    // return data;
  };

  const mutateDocuments = async (details: string) => {
    const mutation = record ? mutations.update.mutation : mutations.create.mutation;
    const variables = record
      ? { updatedDoc: { details }, filter: { id: record.id } }
      : { doc: { details, type: documentType, user: userId } };

    // Mutate files
    return apolloClient.mutate({
      mutation,
      variables,
    });
  };

  const onSubmit = async (v: any) => {
    const { files, ...rest } = v;
    setLoading(true);
    // Mutate documents
    const documentDetails = fields.details.reduce((acc, field) => {
      acc[field] = rest[field];
      return acc;
    }, {} as Record<string, any>);
    try {
      const responseData = await mutateDocuments(JSON.stringify(documentDetails));
      console.log('updated recordForm');
      const documentId = record ? get(record, 'id', '') : get(responseData, 'data.createDocumentEntity.id', '');
      // Mutate files
      const existedFiles: any[] = get(record, 'files') || [];
      const [updatedFiles, deletedFiles] = getUpdatedDeletedFiles(files, existedFiles);
      // upload file
      if (updatedFiles.length > 0) {
        const updatedFilesGetSigned = updatedFiles.map((updatedFile) => {
          const newUpdatedFile = updatedFile;
          newUpdatedFile.documentId = documentId;
          return newUpdatedFile;
        });
        const urlResponse = await getSignedUrlsFunction(updatedFilesGetSigned);
        const urlsData = get(urlResponse, 'data.getManySignedUrls');
        await uploadFile(urlsData, updatedFiles, documentId);
      }
      // delete file
      if (deletedFiles.length > 0) {
        await deleteFile(deletedFiles);
      }

      // if no need to upload new files, show success message
      if (updatedFiles.length === 0) {
        onCompleteFileUploadingAction(true);
        refetchUserData?.();
      }
    } catch (err) {
      onCompleteFileUploadingAction(false, err);
    }
  };

  return (
    <UpdateProfileFormTemplate
      validation={validationSchema}
      userData={record}
      initialFields={fields.form}
      onSubmit={onSubmit}
      onCancel={closeModal}
      loading={loading}
      displaySaveOnFooter
    />
  );
};
export default React.memo(RecordForm);
